'use client';

import React, { useMemo, useState } from 'react';
import type { OHLCV } from '@/lib/api-client';
import { buildEventWindow } from '@/lib/eventWindow';
import { CellBox, MetricToggle, ReactionHeader, type Metric } from './ReactionGrid';

/** Reusable reaction grid: rows = events, columns = ±N trading days
 *  around each event, cells colored by daily close-to-close % change.
 *
 *  Pure presentational. No fetching, no hardcoded ticker, no hardcoded
 *  event type. Pass in:
 *    - bars: daily OHLCV for the instrument you want to read (SPY, QQQ,
 *      IWM, a single stock, …) sorted ascending by date
 *    - events: list of past events with display labels — newest-first
 *      ordering is preserved verbatim
 *
 *  Used by /economic-calendar's SP500 Reaction tab; intended to be
 *  reusable for any future "how did instrument X react around event Y"
 *  visualization. (Issue #232.) */
export interface EventReactionEvent {
  /** YYYY-MM-DD release date — used to anchor the ±window. */
  date: string;
  /** Row label, e.g. "Apr 10, 2026 · CPI". */
  label: string;
  /** Optional secondary label below the primary, e.g. "actual 3.3%". */
  sublabel?: string;
}

interface Props {
  bars: OHLCV[];
  events: EventReactionEvent[];
  before?: number;
  after?: number;
  /** Title rendered above the grid. e.g. "SPY around past CPI releases". */
  title?: string;
  /** D0 cell tooltip text. e.g. "CPI release", "FOMC decision". */
  d0Label?: string;
  /** Default rendering metric. Caller-controlled if needed; otherwise
   *  the component owns metric state via useState. */
  defaultMetric?: Metric;
  /** Empty-state copy when `events` is empty. */
  emptyMessage?: string;
}

const DEFAULT_BEFORE = 5;
const DEFAULT_AFTER = 5;

export function EventReactionWindow({
  bars,
  events,
  before = DEFAULT_BEFORE,
  after = DEFAULT_AFTER,
  title,
  d0Label = 'Event day',
  defaultMetric = 'daily',
  emptyMessage = 'No past events to show.',
}: Props) {
  const [metric, setMetric] = useState<Metric>(defaultMetric);

  return (
    <section className="bg-card-bg border border-default rounded-xl p-4">
      {(title || true) && (
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          {title && <h3 className="text-sm font-semibold text-white">{title}</h3>}
          <div className="ml-auto"><MetricToggle metric={metric} onChange={setMetric} /></div>
        </div>
      )}

      {events.length === 0 ? (
        <p className="text-xs text-fg-subtle py-6 text-center">{emptyMessage}</p>
      ) : (
        <div className="overflow-x-auto">
          <div className="inline-flex flex-col gap-1 min-w-full">
            <ReactionHeader before={before} after={after} d0Tooltip={d0Label} />
            {events.map((ev, idx) => (
              <EventRow
                key={`${ev.date}-${idx}`}
                event={ev}
                bars={bars}
                before={before}
                after={after}
                metric={metric}
                d0Label={d0Label}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}

function EventRow({
  event, bars, before, after, metric, d0Label,
}: {
  event: EventReactionEvent;
  bars: OHLCV[];
  before: number;
  after: number;
  metric: Metric;
  d0Label: string;
}) {
  const cells = useMemo(
    () => buildEventWindow(bars, event.date, before, after),
    [bars, event.date, before, after],
  );
  return (
    <div className="flex gap-1 items-center">
      <div className="w-24 flex-shrink-0 text-xs text-slate-300">
        <div className="font-medium leading-tight">{event.label}</div>
        {event.sublabel && (
          <div className="text-[10px] text-fg-subtle leading-tight">{event.sublabel}</div>
        )}
      </div>
      {cells.map((cell, i) => (
        <CellBox key={i} cell={cell} metric={metric} d0Label={d0Label} />
      ))}
    </div>
  );
}
